<template>
  <v-card outlined class="pa-4" v-if="psp">

    <v-card-title class="headline font-weight-bold pb-2 d-flex align-center">
      <span>Parámetros de stock</span>
      <v-chip small class="ml-3" :color="tipoStockColor" dark>{{ tipoStockLabel }}</v-chip>
    </v-card-title>

    <v-card-text>

      <!-- ── BASE derivado: banner informativo ──────────────── -->
      <v-alert
        v-if="esBaseDerivado"
        type="info"
        text
        dense
        class="mb-4"
        icon="mdi-information-outline"
      >
        El stock de esta presentación se deriva del stock base dividido por el
        factor <strong>÷ {{ psp.presentacion_factor }}</strong>.
        Al ingresar una cantidad, se interpretará en unidades de
        <strong>{{ psp.presentacion_nombre }}</strong> — el backend realiza la conversión.
      </v-alert>

      <!-- ── Control de stock (no se muestra en BASE derivado) ─ -->
      <v-switch
        v-if="!esBaseDerivado"
        v-model="controlaStockLocal"
        color="primary"
        inset
        class="mb-4"
      >
        <template v-slot:label>
          <div class="d-inline-flex align-center">
            <span>Control de stock</span>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small color="primary" v-bind="attrs" v-on="on" class="ml-1">
                  mdi-information
                </v-icon>
              </template>
              <span>
                Activa o desactiva el control de stock para esta presentación.
              </span>
            </v-tooltip>
          </div>
        </template>
      </v-switch>

      <!-- ── Campos de cantidad ─────────────────────────────── -->
      <v-row dense>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model.number="cantidadActualLocal"
            :disabled="!esBaseDerivado && !controlaStockLocal"
            :label="esBaseDerivado ? `Cantidad (en ${psp.presentacion_nombre})` : 'Cantidad actual'"
            outlined
            type="number"
            hide-details
          >
            <template v-if="esBaseDerivado" v-slot:append>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="teal" v-bind="attrs" v-on="on">mdi-sync</v-icon>
                </template>
                <span>El backend convierte este valor al stock base (× {{ psp.presentacion_factor }})</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>

        <!-- stock_min y stock_max solo para tipos que controlan stock propio -->
        <template v-if="!esBaseDerivado">
          <v-col cols="12" sm="4">
            <v-text-field
              v-model.number="stockMinLocal"
              :disabled="!controlaStockLocal"
              label="Stock mínimo"
              outlined
              type="number"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model.number="stockMaxLocal"
              :disabled="!controlaStockLocal"
              label="Stock máximo"
              outlined
              type="number"
              hide-details
            />
          </v-col>
        </template>
      </v-row>

      <v-alert v-if="error" type="error" text dense class="mt-4">{{ error }}</v-alert>

      <v-divider class="my-4"></v-divider>
    </v-card-text>

    <v-card-actions>
      <v-row justify="end">
        <v-btn
          @click="guardar"
          :disabled="!hayCambios || loading"
          :loading="loading"
          rounded
          elevation="2"
          color="primary"
        >
          Guardar cambios
        </v-btn>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import ProductosAPI from "@/api/productos";

export default {
  name: "SucursalProductoInventarioEdit",
  props: {
    productoProp: {
      type: Object,
      required: true,
    },
    psp: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      cantidadActualLocal: null,
      controlaStockLocal: false,
      stockMinLocal: 0,
      stockMaxLocal: 0,
      loading: false,
      error: null,
    };
  },
  computed: {
    esBaseDerivado() {
      return this.psp.tipo_stock === 'BASE' && !this.psp.es_base;
    },
    tipoStockLabel() {
      if (this.psp.tipo_stock === 'TRANSF') return 'Transformación';
      if (this.psp.tipo_stock === 'BASE' && this.psp.es_base) return 'Stock base';
      if (this.psp.tipo_stock === 'BASE' && !this.psp.es_base) return 'Derivado';
      return 'Independiente';
    },
    tipoStockColor() {
      if (this.psp.tipo_stock === 'TRANSF') return 'deep-purple';
      if (this.psp.tipo_stock === 'BASE' && this.psp.es_base) return 'blue darken-1';
      if (this.psp.tipo_stock === 'BASE' && !this.psp.es_base) return 'teal';
      return 'grey darken-1';
    },
    hayCambios() {
      if (this.esBaseDerivado) {
        return this.cantidadActualLocal !== this.psp.cantidad_stock;
      }
      return (
        this.cantidadActualLocal !== this.psp.cantidad_stock ||
        this.controlaStockLocal !== this.psp.controla_stock ||
        this.stockMinLocal !== (this.psp.stock_min || 0) ||
        this.stockMaxLocal !== (this.psp.stock_max || 0)
      );
    },
  },
  methods: {
    initFromPsp(p) {
      this.cantidadActualLocal = p.cantidad_stock;
      this.controlaStockLocal = p.controla_stock;
      this.stockMinLocal = p.stock_min || 0;
      this.stockMaxLocal = p.stock_max || 0;
    },
    guardar() {
      this.loading = true;
      this.error = null;

      const payload = {
        cantidad_actual: this.cantidadActualLocal,
        controla_stock: this.esBaseDerivado ? false : this.controlaStockLocal,
        stock_min: this.esBaseDerivado ? 0 : this.stockMinLocal,
        stock_max: this.esBaseDerivado ? 0 : this.stockMaxLocal,
      };

      ProductosAPI.patchPspStock(this.psp.id, payload)
        .then((response) => {
          this.$emit('actualizarPsp', response.data);
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            button: false,
            color: "success",
            text: "Stock actualizado",
            icon: "mdi-check",
            timeout: 2000,
          });
        })
        .catch(() => {
          this.error = "No se pudo actualizar el stock. Intentá de nuevo.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    psp: {
      handler(newVal) {
        if (newVal) this.initFromPsp(newVal);
      },
      immediate: true,
    },
  },
};
</script>
